import { TypeSystem } from "../../support/type-system.support";
import { CampaignLevel } from "./campaign-level.model";
import { Promotion } from "./promotion.model";

    /**
     * @description
     *  Modelo de una campaña.
     *
     * @see gigot-backend/Models/Sales/Campaign.cs
     */

export class Campaign {

    public campaignLevels: CampaignLevel[];
    public endDate: Date;
    public endDateCC: Date;
    public endDatePick: Date;
    public estimatedOrders: number;
    public fifthOrderCredit: number;
    public firstOrderCredit: number;
    public fourthOrderCredit: number;
    public id: number;
    public minOrder: number;
    public minOrderShipping: number;
    public number: string;
    public promotions: Promotion[];
    public secondOrderCredit: number;
    public sequence: number;
    public shippingAmount: number;
    public startDate: Date;
    public startDateCC: Date;
    public startDatePick: Date;
    public status: string;
    public thirdOrderCredit: number;

    /**
     * @description
     *	Construye una campaña con una semántica vacía. Nótese que no es lo
     *  mismo que construir una campaña con valores por defecto. Las reglas
     *  generales de construcción son:
     *
     *      - Campos 'array' se definen como [].
     *      - Campos 'boolean' se definen como 'false'.
     *      - Campos 'Date' se definen como 'undefined'.
     *      - Campos 'enum' se definen como 'undefined'.
     *      - Campos 'number' se definen como 'undefined'.
     *      - Campos 'string' se definen como '' (cadena vacía).
     *      - Campos 'object' se definen llamando a su propio método #empty().
     *      - Campos de la superclase se definen al igual que campos 'object'.
     *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
     *
     * @returns
     *	Una campaña vacía.
     */
    public static empty(): Campaign {
        const campaign: Campaign = new Campaign();
        campaign.campaignLevels = [];
        campaign.endDate = undefined;
        campaign.endDateCC = undefined;
        campaign.endDatePick = undefined;
        campaign.estimatedOrders = undefined;
        campaign.fifthOrderCredit = undefined;
        campaign.firstOrderCredit = undefined;
        campaign.fourthOrderCredit = undefined;
        campaign.id = undefined;
        campaign.minOrder = undefined;
        campaign.minOrderShipping = undefined;
        campaign.number = "";
        campaign.promotions = [];
        campaign.secondOrderCredit = undefined;
        campaign.sequence = undefined;
        campaign.shippingAmount = undefined;
        campaign.startDate = undefined;
        campaign.startDateCC = undefined;
        campaign.startDatePick = undefined;
        campaign.status = "";
        campaign.thirdOrderCredit = undefined;
        return campaign;
    }

	/**
     * @description
     *  Método de reificación de un modelo compatible anémico. Esto permite
     *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
     *  valor de sus propiedades se mantiene.
     *
     * @param anemicCampaign
     *  El modelo anémico a reificar.
     *
     * @returns
     *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
     *  profundidad desde el modelo anémico.
     */
	public static from(anemicCampaign: Campaign): Campaign {
		if (!anemicCampaign) return undefined;
        const campaign: Campaign = Campaign.empty();
		campaign.campaignLevels = TypeSystem.orElse(anemicCampaign.campaignLevels, [])
			.map(campaignLevel => CampaignLevel.from(campaignLevel));
        campaign.endDate = new Date(anemicCampaign.endDate);
        campaign.endDateCC = new Date(anemicCampaign.endDateCC);
        campaign.endDatePick = new Date(anemicCampaign.endDatePick);
        campaign.estimatedOrders = anemicCampaign.estimatedOrders;
        campaign.fifthOrderCredit = anemicCampaign.fifthOrderCredit;
        campaign.firstOrderCredit = anemicCampaign.firstOrderCredit;
        campaign.fourthOrderCredit = anemicCampaign.fourthOrderCredit;
        campaign.id = anemicCampaign.id;
        campaign.minOrder = anemicCampaign.minOrder;
        campaign.minOrderShipping = anemicCampaign.minOrderShipping;
        campaign.number = anemicCampaign.number;
        campaign.promotions = TypeSystem.orElse(anemicCampaign.promotions, [])
			.map(promotion => Promotion.from(promotion));
        campaign.secondOrderCredit = anemicCampaign.secondOrderCredit;
        campaign.sequence = anemicCampaign.sequence;
        campaign.shippingAmount = anemicCampaign.shippingAmount;
        campaign.startDate = new Date(anemicCampaign.startDate);
        campaign.startDateCC = new Date(anemicCampaign.startDateCC);
        campaign.startDatePick = new Date(anemicCampaign.startDatePick);
        campaign.status = anemicCampaign.status;
        campaign.thirdOrderCredit = anemicCampaign.thirdOrderCredit;
		return campaign;
	}

	/**
	 * @description
	 *	Determina si una fecha pertenece a esta campaña.
	 *
	 * @param date
	 *	La fecha a probar.
	 *
	 * @returns
	 *	Verdadero si la fecha se encuentra entre la fecha de inciio y fin de
	 *	esta campaña, falso de otro modo.
	 */
	public contains(date: Date): boolean {
		return this.startDate <= date && date <= this.endDate;
	}
}
